import {LobbyImage} from '@colonist/types'
import {GameTypeEnum} from '@colonist/game-base-types'
import {DropdownContent, UIComponentDropdownController} from '../../ui_components/UIComponentDropdownController'
import {createDivIn} from '../../ui_utils/DomUtils'
import {UIGameManager} from '../UIGameManager'
import {UIGameHelpButtonsController} from './UIGameHelpButtonsController'
import UIGameLeaveButton from './UIGameLeaveButton'
import {TranslationObject} from '@colonist/i18n'
import UIGameRulesButton from './UIGameRulesButton'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {uiGameManager} from '../../EntryShared'
import {UIGameHelpButton} from './UIGameHelpButton'

export default class UIGameSettingsButton extends UIGameHelpButton {
    static dropdownParentId = 'ingame_settings_button'

    private readonly dropdownContainer: HTMLDivElement
    private dropdownValues: DropdownContent[]

    private readonly dropdown: UIComponentDropdownController

    protected readonly uiGameManager: UIGameManager

    constructor(uiGameHelpButtonController: UIGameHelpButtonsController, parent: HTMLDivElement) {
        super(uiGameHelpButtonController, parent, 'settings-button', LobbyImage.Settings)

        this.uiGameManager = this.uiGameHelpButtonController.uiGameManager

        this.gameHelpButtonContainer.id = UIGameSettingsButton.dropdownParentId

        this.dropdownContainer = createDivIn(this.gameHelpButtonContainer, 'component-dropdown-container')

        const isPlayerSpectator = this.uiGameManager.gameController.isPlayerSpectator
        this.dropdownValues = this.getDropdownValues(isPlayerSpectator)

        createDivIn(this.dropdownContainer, 'dummy_item')
        createDivIn(this.dropdownContainer, 'component-dropdown-content')

        this.dropdown = new UIComponentDropdownController(this.dropdownValues, UIGameSettingsButton.dropdownParentId, false, this.gameHelpButtonContainer)
    }

    updateDropdownValues() {
        const isPlayerSpectator = this.uiGameManager.gameController.isPlayerSpectator
        this.dropdownValues = this.getDropdownValues(isPlayerSpectator)
        this.dropdown.updateDropdownState(this.dropdownValues)
    }

    protected getDropdownValues(isPlayerSpectator: boolean): DropdownContent[] {
        const showResignGame = this.uiGameManager.gameState.canResign
        const leaveGameText: TranslationObject = showResignGame ? {key: 'strings:game.gearIcon.resignGame'} : {key: 'strings:game.gearIcon.leaveGame'}

        const dropdownContent: DropdownContent[] = []

        dropdownContent.push(this.settingsDropdownItem())
        if(UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI) {
            dropdownContent.push(this.gameInfoDropdownItem())
        }

        if(this.shouldIncludePauseGame()) dropdownContent.push(this.pauseGameDropdownItem(isPlayerSpectator))

        if(this.shouldIncludeDisableKarma()) dropdownContent.push(this.disableKarmaDropdownItem(isPlayerSpectator))
        if(UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI) {
            dropdownContent.push(this.rulebookDropdownItem())
        }
        dropdownContent.push(this.leaveGameDropdownItem(leaveGameText))

        return dropdownContent
    }

    private settingsDropdownItem(): DropdownContent {
        return {
            id: 'ingame_setting',
            text: {key: 'strings:game.gearIcon.settings'},
            class: '',
            action: () => {
                this.uiGameManager.pauseController.pauseIfSinglePlayer()
                this.uiGameManager.settingsController.toggleSettings(true)
            },
        }
    }

    private pauseGameDropdownItem(isPlayerSpectator: boolean): DropdownContent {
        return {
            id: 'ingame_pause_game',
            text: {key: 'strings:game.gearIcon.pauseGame'},
            class: isPlayerSpectator ? 'component-dropdown-disabled' : '',
            action: () => {
                this.uiGameManager.pauseController.onAutoPauseTriggered()
            },
        }
    }

    private disableKarmaDropdownItem(isPlayerSpectator: boolean): DropdownContent {
        return {
            id: 'ingame_disable_karma',
            text: {key: 'strings:game.gearIcon.disableKarma'},
            class: isPlayerSpectator ? 'component-dropdown-disabled' : '',
            action: () => this.uiGameManager.socketGameSend.submitChat('/disablekarma'),
        }
    }

    private leaveGameDropdownItem(leaveGameText: TranslationObject): DropdownContent {
        return {
            id: 'ingame_go_to_lobby',
            text: leaveGameText,
            class: 'component-dropdown-red',
            action: () => {
                UIGameLeaveButton.clicked(this.uiGameManager)
            },
        }
    }

    private rulebookDropdownItem(): DropdownContent {
        return {
            id: 'ingame_open_rulebook',
            text: {key: 'strings:rulebook.title'},
            class: '',
            action: () => {
                UIGameRulesButton.openRulebook(this.uiGameManager.gameController.gameSettings.modeSetting)
            },
        }
    }

    private gameInfoDropdownItem(): DropdownContent {
        return {
            id: 'ingame_open_information',
            text: {key: 'strings:game.settings.gameInfoTab.title'},
            class: '',
            action: () =>
                this.uiGameManager.eventController.sendEvent(true, GameStateEnums.UIGameEventEnums.GameInformationPopupActive)
            ,
        }
    }

    private shouldIncludeDisableKarma(): boolean {
        if(this.uiGameManager.gameController.isReplay) return false

        const gameTypesWithoutKarma = [GameTypeEnum.BotGame, GameTypeEnum.BeginnerMode]
        const isLocalDiscordGame = this.uiGameManager.gameController.gameSettings.channelId != undefined

        if(gameTypesWithoutKarma.includes(this.uiGameManager.gameController.gameSettings.gameType)) return false
        if(isLocalDiscordGame) return false
        return true
    }

    private shouldIncludePauseGame(): boolean {
        if(this.uiGameManager.gameController.isReplay) return false

        return true
    }

    static updatePauseState = (state: boolean, isPlayerSpectator: boolean) => {
        if(isPlayerSpectator) return

        const pauseButton = document.getElementById('ingame_pause_game')
        if(!pauseButton) return

        const action = state ? 'add' : 'remove'
        pauseButton.classList[action]('component-dropdown-disabled')
    }

    static toggleSettingsMenu = (isVisible: boolean) => {
        const upperLeftSection = document.getElementById('upper-left-container')
        if(!upperLeftSection) return
        uiGameManager.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.ToggleChatFeed)
        uiGameManager.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.ToggleLogFeed)
        upperLeftSection.style.display = isVisible ? 'none' : ''
    }
}
